import React from "react";
import { Phone, PhoneOff, Users } from "lucide-react";
import { Button } from "../ui";
import type { CallBrief } from "../../api/callsAPI";
import { formatDuration, formatPhone, reasonLabel } from "./callFormat";

export interface IncomingCallCardProps {
  /** From the call's own parameters: shown the instant it rings. */
  callerLabel: string | null;
  reason: string | null;
  /** The full brief from the backend, once it arrives. */
  brief: CallBrief | null;
  onAccept: () => void;
  onDecline: () => void;
  onOpenFamily: (path: string) => void;
}

/**
 * The ringing card, top right. It does not block the page: staff can finish
 * what they were typing and still answer.
 */
const IncomingCallCard: React.FC<IncomingCallCardProps> = ({
  callerLabel,
  reason,
  brief,
  onAccept,
  onDecline,
  onOpenFamily,
}) => {
  const label = brief?.caller.label || callerLabel || "Unknown caller";
  const number = brief?.caller.number ? formatPhone(brief.caller.number) : "";
  const why = reasonLabel(brief?.escalation.reason || reason);
  const summary = brief?.escalation.summary || "";
  const urgent = brief?.escalation.urgency === "urgent";
  const familyPath = brief?.caller.family_path || null;

  return (
    <div
      role="alertdialog"
      aria-label={`Incoming call from ${label}`}
      aria-live="assertive"
      className="fixed top-4 right-4 z-[70] w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-4"
    >
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
        <Phone className="w-4 h-4 animate-pulse" aria-hidden="true" />
        Incoming call
        {urgent && (
          <span className="ml-auto rounded-full bg-red-100 dark:bg-red-900/40 px-2 py-0.5 text-red-700 dark:text-red-300 normal-case">
            Urgent
          </span>
        )}
      </div>
      <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">{label}</p>
      {number && number !== label && (
        <p className="text-sm text-gray-600 dark:text-gray-300">{number}</p>
      )}
      {why && <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">About: {why}</p>}
      {summary && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 line-clamp-4">{summary}</p>
      )}
      {brief && brief.waited_seconds > 0 && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Waiting {formatDuration(brief.waited_seconds)}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" size="md" icon={<Phone className="w-4 h-4" />} onClick={onAccept}>
          Accept
        </Button>
        <Button variant="danger" size="md" icon={<PhoneOff className="w-4 h-4" />} onClick={onDecline}>
          Decline
        </Button>
        {familyPath && (
          <Button
            variant="ghost"
            size="md"
            icon={<Users className="w-4 h-4" />}
            onClick={() => onOpenFamily(familyPath)}
          >
            Open family
          </Button>
        )}
      </div>
    </div>
  );
};

export default IncomingCallCard;
